import { Product, CategoryMeta } from './types';

// ─── Business Config ────────────────────────────────────────────────
// Single source of truth for business details used across the site
export const BUSINESS = {
  name: 'The Yaad Shop',
  legalName: 'The Yaad Shop',
  email: 'info@theyaadshop.com',
  eTransferEmail: 'info@theyaadshop.com',  // UPDATE: set your Interac e-Transfer email
  phone: '',                                // UPDATE: set your phone number
  whatsapp: '',                             // UPDATE: set your WhatsApp number
  website: 'https://theyaadshop.com',
  address: {
    street: '',
    city: 'Toronto',
    province: 'ON',
    postalCode: '',
    country: 'CA',
  },
  // Shipping origin (used for eShipper API quotes)
  shippingOrigin: {
    name: 'The Yaad Shop',
    street1: '',                            // UPDATE: set your warehouse address
    city: 'Toronto',
    state: 'ON',
    zip: '',                                // UPDATE: set your postal code
    country: 'CA',
    phone: '',                              // UPDATE: set your phone
  },
  hstRate: 0.13,
  orderPrefix: 'YS',
} as const;

// ─── Category Definitions ───────────────────────────────────────────
export const CATEGORIES: CategoryMeta[] = [
  { name: 'Machetes', emoji: '🔪', description: 'Blades & agricultural tools', mixAndMatch: false },
  { name: 'Flag Bandanas', emoji: '🏴', description: 'Bandanas, scarves & wraps', mixAndMatch: true, mixAndMatchNote: 'Mix any flag designs within a box' },
  { name: 'Caps & Fishing Hats', emoji: '🧢', description: 'Snapbacks, bucket hats, visors & more', mixAndMatch: true, mixAndMatchNote: 'Mix flags within style' },
  { name: 'Keychains & Collectables', emoji: '🔑', description: 'Flag keychains, bottle openers & novelty collectables', mixAndMatch: true, mixAndMatchNote: 'Mix any designs within a box' },
  { name: 'Flag Sunglasses', emoji: '🕶️', description: 'Country flag print sunglasses', mixAndMatch: true, mixAndMatchNote: 'Mix any flag designs within a box' },
  { name: "Women's Wear", emoji: '👗', description: 'Caribbean-inspired dresses, jewelry & accessories', mixAndMatch: true, mixAndMatchNote: 'Mix any styles within a box' },
  { name: 'Smudge Sticks', emoji: '🌿', description: 'Sage, palo santo & spiritual cleansing', mixAndMatch: false },
  { name: 'Full Size Flags', emoji: '🚩', description: 'Large country flags', mixAndMatch: true, mixAndMatchNote: 'Mix any country flags within a box' },
  { name: 'Car Mirror Trinkets', emoji: '🚗', description: 'Rearview mirror hanging accessories', mixAndMatch: true, mixAndMatchNote: 'Mix any flag designs within a box' },
  { name: 'Windbreakers', emoji: '🧥', description: 'Flag print windbreaker jackets', mixAndMatch: false },
];

// ─── Product Catalogue ──────────────────────────────────────────────
export const PRODUCTS: Product[] = [
  // ── MACHETES ──────────────────────────────────────────────────────
  {
    id: 'machete-3-canales',
    sku: 'MCH-3CN',
    name: '3 Canales Machete',
    category: 'Machetes',
    subcategory: '3 Canales',
    price: 17.50,
    msrp: 29.99,
    stock: 200,
    weight: 1.6,
    image: '/images/products/machete-3-canales.webp',
    description: 'Latin-style machete featuring three blood grooves (canales) along the blade for reduced weight and suction. Available in 20" and 22" sizes. A staple of Central and South American agriculture.',
    specs: { sizes: '20" / 22"', material: 'Carbon Steel', handle: 'Wood', style: '3 Canales' },
    boxQty: 6,
    moq: 12,
    unit: 'per case of 6',
  },
  {
    id: 'machete-rozador',
    sku: 'MCH-RZD',
    name: 'Rozador Machete',
    category: 'Machetes',
    subcategory: 'Rozador',
    price: 17.50,
    msrp: 32.99,
    stock: 150,
    weight: 1.6,
    image: '/images/products/machete-rozador.webp',
    description: 'The Rozador features a distinctive curved tip designed for land clearing and brush removal. Available in 18" and 21" sizes. Perfect for farmers and groundskeepers tackling overgrown terrain.',
    specs: { sizes: '18" / 21"', material: 'Carbon Steel', handle: 'Wood', style: 'Rozador' },
    boxQty: 6,
    moq: 12,
    unit: 'per case of 6',
  },
  {
    id: 'machete-rula',
    sku: 'MCH-RUL',
    name: 'Rula Machete',
    category: 'Machetes',
    subcategory: 'Rula',
    price: 17.50,
    msrp: 29.99,
    stock: 100,
    weight: 1.4,
    image: '/images/products/machete-rula.webp',
    description: 'The Rula is a lightweight machete designed for cutting soft vegetation and grasses. Available in 20" and 22" sizes. Its slim profile makes it ideal for extended use without fatigue.',
    specs: { sizes: '20" / 22"', material: 'Carbon Steel', handle: 'Wood', style: 'Rula' },
    boxQty: 6,
    moq: 12,
    unit: 'per case of 6',
  },
  {
    id: 'machete-barrigon',
    sku: 'MCH-BAR',
    name: 'Barrigón Machete',
    category: 'Machetes',
    subcategory: 'Barrigón',
    price: 17.50,
    msrp: 34.99,
    stock: 80,
    weight: 1.8,
    image: '/images/products/machete-barrigon.webp',
    description: 'The Barrigón features a pronounced belly curve in the blade, traditionally used for sugar cane harvesting. Its weighted forward design delivers powerful chopping strokes with minimal effort.',
    specs: { blade: '14"', material: 'Carbon Steel', handle: 'Wood', style: 'Barrigón' },
    boxQty: 6,
    moq: 12,
    unit: 'per case of 6',
  },
  {
    id: 'machete-pacora',
    sku: 'MCH-PAC',
    name: 'Pacora Machete',
    category: 'Machetes',
    subcategory: 'Pacora',
    price: 17.50,
    msrp: 32.99,
    stock: 150,
    weight: 1.3,
    image: '/images/products/machete-pacora.webp',
    description: 'The Pacora is a precision machete crafted for delicate agricultural tasks. Its narrow, balanced blade provides excellent control for pruning, trimming, and detailed work around crops.',
    specs: { blade: '16"', material: 'Carbon Steel', handle: 'Wood', style: 'Pacora' },
    boxQty: 6,
    moq: 12,
    unit: 'per case of 6',
  },
  {
    id: 'machete-bananera',
    sku: 'MCH-BNA',
    name: 'Bananera Machete',
    category: 'Machetes',
    subcategory: 'Bananera',
    price: 17.50,
    msrp: 27.99,
    stock: 100,
    weight: 0.9,
    image: '/images/products/machete-bananera.webp',
    description: 'The Bananera is a compact machete designed for fruit harvesting, especially bananas and plantains. Available in 10" and 12" sizes. Its short blade offers precision in tight plantation rows.',
    specs: { sizes: '10" / 12"', material: 'Carbon Steel', handle: 'Wood', style: 'Bananera' },
    boxQty: 6,
    moq: 12,
    unit: 'per case of 6',
  },
  {
    id: 'machete-guaduero',
    sku: 'MCH-GDR',
    name: 'Guaduero Machete',
    category: 'Machetes',
    subcategory: 'Guaduero',
    price: 17.50,
    msrp: 38.99,
    stock: 60,
    weight: 2.0,
    image: '/images/products/machete-guaduero.webp',
    description: 'The Guaduero is a heavy-duty bamboo cleaver machete. Its thick, reinforced blade is purpose-built for splitting bamboo (guadua) and hardwood. A must-have tool for tropical construction and woodworking.',
    specs: { blade: '16"', material: 'High Carbon Steel', handle: 'Wood', style: 'Guaduero' },
    boxQty: 6,
    moq: 12,
    unit: 'per case of 6',
  },
  {
    id: 'machete-cuma',
    sku: 'MCH-CMA',
    name: 'Cuma Machete',
    category: 'Machetes',
    subcategory: 'Cuma',
    price: 17.50,
    msrp: 29.99,
    stock: 50,
    weight: 1.2,
    image: '/images/products/machete-cuma.webp',
    description: 'The Cuma is a hawkbill-style reaping machete with a curved inward blade. Available in 18" and 20" sizes. Designed for harvesting rice, wheat, and other grains with a natural pulling motion.',
    specs: { sizes: '18" / 20"', material: 'Carbon Steel', handle: 'Wood', style: 'Cuma / Hawkbill' },
    boxQty: 6,
    moq: 12,
    unit: 'per case of 6',
  },
  {
    id: 'machete-recto',
    sku: 'MCH-RCT',
    name: 'Recto Machete',
    category: 'Machetes',
    subcategory: 'Recto',
    price: 17.50,
    msrp: 34.99,
    stock: 70,
    weight: 1.5,
    image: '/images/products/machete-recto.webp',
    description: 'The Recto is a modern straight-blade machete with protective black coatings for corrosion resistance. Available in 18" and 20" sizes. A tactical-style utility blade for both field and outdoor use.',
    specs: { sizes: '18" / 20"', material: 'Black Coated Carbon Steel', handle: 'Polymer', style: 'Recto' },
    boxQty: 6,
    moq: 12,
    unit: 'per case of 6',
  },
  {
    id: 'machete-colima',
    sku: 'MCH-CLM',
    name: 'Colima Machete',
    category: 'Machetes',
    subcategory: 'Colima',
    price: 17.50,
    msrp: 32.99,
    stock: 60,
    weight: 1.3,
    image: '/images/products/machete-colima.webp',
    description: 'The Colima is a double-edged machete designed for grass mowing and field clearing. Available in 18" and 22" sizes. Both edges are sharpened for efficient back-and-forth cutting strokes.',
    specs: { sizes: '18" / 22"', material: 'Carbon Steel', handle: 'Wood', style: 'Colima / Double-Edge' },
    boxQty: 6,
    moq: 12,
    unit: 'per case of 6',
  },
  {
    id: 'machete-barrigon-pesado',
    sku: 'MCH-BRP',
    name: 'Barrigón Pesado Machete',
    category: 'Machetes',
    subcategory: 'Barrigón Pesado',
    price: 17.50,
    msrp: 42.99,
    stock: 120,
    weight: 2.2,
    image: '/images/products/machete-barrigon-pesado.webp',
    description: 'The Barrigón Pesado is a hybrid machete/axe combining the belly curve of the Barrigón with extra blade weight. Built for heavy chopping tasks including thick brush, small trees, and dense tropical growth.',
    specs: { blade: '18"', material: 'High Carbon Steel', handle: 'Wood', style: 'Barrigón Pesado' },
    boxQty: 6,
    moq: 12,
    unit: 'per case of 6',
  },
  {
    id: 'machete-bamba',
    sku: 'MCH-BMB',
    name: 'Bamba Machete',
    category: 'Machetes',
    subcategory: 'Bamba',
    price: 17.50,
    msrp: 49.99,
    stock: 100,
    weight: 1.6,
    image: '/images/products/machete-bamba.webp',
    description: 'The Bamba is a modern katana-inspired machete with a sleek curved blade design. Combines traditional Latin machete utility with a contemporary tactical aesthetic. A standout in any collection.',
    specs: { blade: '18"', material: 'Carbon Steel', handle: 'Ergonomic Composite', style: 'Bamba / Katana' },
    boxQty: 6,
    moq: 12,
    unit: 'per case of 6',
  },
  {
    id: 'machete-mill-file-box',
    sku: 'MCH-MFB',
    name: 'Mill File Box (12 Pack)',
    category: 'Machetes',
    subcategory: 'Accessories',
    price: 51.00,
    msrp: 89.99,
    stock: 300,
    weight: 6.0,
    image: '/images/products/machete-mill-file.webp',
    description: 'Box of 12 professional mill files for machete sharpening and edge maintenance. Heavy-duty cut steel with ergonomic handles. Essential accessory for any machete retailer.',
    specs: { quantity: '12 per box', length: '10"', material: 'Mill-Cut Steel', handle: 'Plastic Grip' },
    boxQty: 1,
    moq: 1,
    unit: 'per box of 12',
  },
  {
    id: 'okapi-ratchet-knife',
    sku: 'MCH-OKR',
    name: 'Okapi Ratchet Knife (10 Pack)',
    category: 'Machetes',
    subcategory: 'Accessories',
    price: 40.00,
    msrp: 89.99,
    stock: 200,
    weight: 2.5,
    image: '/images/products/okapi-ratchet-knife.webp',
    description: 'Pack of 10 Okapi ratchet folding knives. Classic South African design with decorative wood and metal handle and reliable ratchet locking mechanism. A proven bestseller in multicultural retail.',
    specs: { quantity: '10 per pack', blade: '4"', material: 'Stainless Steel', handle: 'Rosewood + Metal', style: 'Ratchet Folding' },
    boxQty: 1,
    moq: 1,
    unit: 'per pack of 10',
  },

  // ── FLAG BANDANAS ─────────────────────────────────────────────────
  {
    id: 'bandana-tt',
    sku: 'BND-TT',
    name: 'Trinidad & Tobago Flag Bandana',
    category: 'Flag Bandanas',
    price: 2.50,
    msrp: 6.99,
    stock: 400,
    weight: 0.05,
    image: '/images/products/bandana-trinidad.webp',
    description: 'Trinidad & Tobago flag print bandana. 100% cotton, 22x22 inches.',
    specs: { material: '100% Cotton', size: '22x22"' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },
  {
    id: 'bandana-jm',
    sku: 'BND-JM',
    name: 'Jamaica Flag Bandana',
    category: 'Flag Bandanas',
    price: 2.50,
    msrp: 6.99,
    stock: 400,
    weight: 0.05,
    image: '/images/products/bandana-jamaica.webp',
    description: 'Jamaica flag print bandana. 100% cotton, 22x22 inches.',
    specs: { material: '100% Cotton', size: '22x22"' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },
  {
    id: 'bandana-bb',
    sku: 'BND-BB',
    name: 'Barbados Flag Bandana',
    category: 'Flag Bandanas',
    price: 2.50,
    msrp: 6.99,
    stock: 400,
    weight: 0.05,
    image: '/images/products/bandana-barbados.webp',
    description: 'Barbados flag print bandana. 100% cotton, 22x22 inches.',
    specs: { material: '100% Cotton', size: '22x22"' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },
  {
    id: 'bandana-gy',
    sku: 'BND-GY',
    name: 'Guyana Flag Bandana',
    category: 'Flag Bandanas',
    price: 2.50,
    msrp: 6.99,
    stock: 400,
    weight: 0.05,
    image: '/images/products/bandana-guyana.webp',
    description: 'Guyana flag print bandana. 100% cotton, 22x22 inches.',
    specs: { material: '100% Cotton', size: '22x22"' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },
  {
    id: 'bandana-ag',
    sku: 'BND-AG',
    name: 'Antigua & Barbuda Flag Bandana',
    category: 'Flag Bandanas',
    price: 2.50,
    msrp: 6.99,
    stock: 400,
    weight: 0.05,
    image: '/images/products/bandana-antigua.webp',
    description: 'Antigua & Barbuda flag print bandana. 100% cotton, 22x22 inches.',
    specs: { material: '100% Cotton', size: '22x22"' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },
  {
    id: 'bandana-dm',
    sku: 'BND-DM',
    name: 'Dominica Flag Bandana',
    category: 'Flag Bandanas',
    price: 2.50,
    msrp: 6.99,
    stock: 400,
    weight: 0.05,
    image: '/images/products/bandana-dominica.webp',
    description: 'Dominica flag print bandana. 100% cotton, 22x22 inches.',
    specs: { material: '100% Cotton', size: '22x22"' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },
  {
    id: 'bandana-lc',
    sku: 'BND-LC',
    name: 'St. Lucia Flag Bandana',
    category: 'Flag Bandanas',
    price: 2.50,
    msrp: 6.99,
    stock: 400,
    weight: 0.05,
    image: '/images/products/bandana-stlucia.webp',
    description: 'St. Lucia flag print bandana. 100% cotton, 22x22 inches.',
    specs: { material: '100% Cotton', size: '22x22"' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },
  {
    id: 'bandana-gd',
    sku: 'BND-GD',
    name: 'Grenada Flag Bandana',
    category: 'Flag Bandanas',
    price: 2.50,
    msrp: 6.99,
    stock: 400,
    weight: 0.05,
    image: '/images/products/bandana-grenada.webp',
    description: 'Grenada flag print bandana. 100% cotton, 22x22 inches.',
    specs: { material: '100% Cotton', size: '22x22"' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },
  {
    id: 'bandana-ht',
    sku: 'BND-HT',
    name: 'Haiti Flag Bandana',
    category: 'Flag Bandanas',
    price: 2.50,
    msrp: 6.99,
    stock: 400,
    weight: 0.05,
    image: '/images/products/bandana-haiti.webp',
    description: 'Haiti flag print bandana. 100% cotton, 22x22 inches.',
    specs: { material: '100% Cotton', size: '22x22"' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },

  // ── CAPS & FISHING HATS ──────────────────────────────────────────
  {
    id: 'cap-jm',
    sku: 'CAP-JM',
    name: 'Jamaica Flag Cap',
    category: 'Caps & Fishing Hats',
    subcategory: 'Baseball Caps',
    price: 5.00,
    msrp: 14.99,
    stock: 200,
    weight: 0.12,
    image: '/images/products/cap-jamaica.webp',
    description: 'Jamaica flag embroidered baseball cap. Adjustable strap, one size fits most.',
    specs: { style: 'Baseball Cap', closure: 'Adjustable Strap', size: 'One Size' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },
  {
    id: 'cap-tt',
    sku: 'CAP-TT',
    name: 'Trinidad & Tobago Flag Cap',
    category: 'Caps & Fishing Hats',
    subcategory: 'Baseball Caps',
    price: 5.00,
    msrp: 14.99,
    stock: 150,
    weight: 0.12,
    image: '/images/products/cap-trinidad.webp',
    description: 'Trinidad & Tobago flag embroidered baseball cap. Adjustable strap, one size fits most.',
    specs: { style: 'Baseball Cap', closure: 'Adjustable Strap', size: 'One Size' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },
  {
    id: 'fishhat-jm',
    sku: 'FSH-JM',
    name: 'Jamaica Flag Fishing Hat',
    category: 'Caps & Fishing Hats',
    subcategory: 'Fishing Hats',
    price: 6.00,
    msrp: 16.99,
    stock: 120,
    weight: 0.1,
    image: '/images/products/bucket-hat-jamaica.webp',
    description: 'Jamaica flag print bucket-style fishing hat. Full brim sun protection, lightweight.',
    specs: { style: 'Bucket / Fishing Hat', size: 'One Size', brim: 'Full' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },
  {
    id: 'fishhat-tt',
    sku: 'FSH-TT',
    name: 'Trinidad & Tobago Fishing Hat',
    category: 'Caps & Fishing Hats',
    subcategory: 'Fishing Hats',
    price: 6.00,
    msrp: 16.99,
    stock: 100,
    weight: 0.1,
    image: '/images/products/bucket-hat-trinidad.webp',
    description: 'Trinidad & Tobago flag print bucket-style fishing hat. Full brim sun protection.',
    specs: { style: 'Bucket / Fishing Hat', size: 'One Size', brim: 'Full' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },

  // ── KEYCHAINS & COLLECTABLES ─────────────────────────────────────
  {
    id: 'keychain-jm',
    sku: 'KEY-JM',
    name: 'Jamaica Flag Keychain',
    category: 'Keychains & Collectables',
    price: 1.50,
    msrp: 4.99,
    stock: 500,
    weight: 0.03,
    image: '/images/products/keychain-jamaica.webp',
    description: 'Jamaica flag metal keychain with ring clip. Durable enamel finish.',
    specs: { material: 'Metal / Enamel', size: '2"' },
    boxQty: 24,
    moq: 24,
    unit: 'per box of 24',
  },

  // ── FLAG SUNGLASSES ───────────────────────────────────────────────
  {
    id: 'sunglass-jm',
    sku: 'SUN-JM',
    name: 'Jamaica Flag Sunglasses',
    category: 'Flag Sunglasses',
    price: 3.00,
    msrp: 9.99,
    stock: 300,
    weight: 0.04,
    image: '/images/products/sunglass-jamaica.webp',
    description: 'Jamaica flag print sunglasses. UV400 protection, lightweight plastic frame.',
    specs: { protection: 'UV400', frame: 'Plastic', style: 'Wayfarer' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },
  {
    id: 'sunglass-tt',
    sku: 'SUN-TT',
    name: 'Trinidad & Tobago Flag Sunglasses',
    category: 'Flag Sunglasses',
    price: 3.00,
    msrp: 9.99,
    stock: 250,
    weight: 0.04,
    image: '/images/products/sunglass-trinidad.webp',
    description: 'Trinidad & Tobago flag print sunglasses. UV400 protection, lightweight plastic frame.',
    specs: { protection: 'UV400', frame: 'Plastic', style: 'Wayfarer' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },

  // ── FLAG HOOP EARRINGS ────────────────────────────────────────────

  // ── SMUDGE STICKS ────────────────────────────────────────────────
  {
    id: 'smudge-white-sage-sm',
    sku: 'SMG-WS-S',
    name: 'White Sage Smudge Stick (Small)',
    category: 'Smudge Sticks',
    subcategory: 'White Sage',
    price: 3.00,
    msrp: 8.99,
    stock: 300,
    weight: 0.06,
    image: '/images/products/smudge-sage-small.webp',
    description: 'Small white sage smudge stick, approx. 4 inches. Used for spiritual cleansing and purification.',
    specs: { type: 'White Sage', size: '4"', origin: 'California' },
    boxQty: 24,
    moq: 24,
    unit: 'per box of 24',
  },
  {
    id: 'smudge-white-sage-lg',
    sku: 'SMG-WS-L',
    name: 'White Sage Smudge Stick (Large)',
    category: 'Smudge Sticks',
    subcategory: 'White Sage',
    price: 5.00,
    msrp: 12.99,
    stock: 200,
    weight: 0.1,
    image: '/images/products/smudge-sage-medium.webp',
    description: 'Large white sage smudge stick, approx. 8-9 inches. For deep cleansing rituals and ceremonies.',
    specs: { type: 'White Sage', size: '8-9"', origin: 'California' },
    boxQty: 12,
    moq: 12,
    unit: 'per box of 12',
  },
  {
    id: 'smudge-palo-santo',
    sku: 'SMG-PS',
    name: 'Palo Santo Sticks',
    category: 'Smudge Sticks',
    subcategory: 'Palo Santo',
    price: 4.00,
    msrp: 10.99,
    stock: 250,
    weight: 0.08,
    image: '/images/products/smudge-palo-santo.webp',
    description: 'Palo Santo (Holy Wood) sticks for spiritual cleansing. Sweet, woodsy fragrance. Ethically sourced.',
    specs: { type: 'Palo Santo', size: '4"', origin: 'South America' },
    boxQty: 24,
    moq: 24,
    unit: 'per box of 24',
  },



  // ── SCARVES & WRAPS (in Flag Bandanas category) ─────────────────────
  {
    id: 'mesh-beach-jm',
    sku: 'MSH-BJ',
    name: 'Jamaica Mesh Beach Dress',
    category: "Women's Wear",
    subcategory: 'Beach Dresses',
    price: 3.50,
    msrp: 9.99,
    stock: 200,
    weight: 0.06,
    image: '/images/products/scarf-jamaica-mesh.webp',
    description: 'Jamaica flag colors mesh beach dress. Lightweight, breathable Caribbean-inspired cover-up.',
    specs: { material: 'Polyester Mesh', size: 'One Size' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },
  {
    id: 'mesh-beach-rasta',
    sku: 'MSH-BR',
    name: 'Rasta Bob Marley Mesh Cover-Up',
    category: "Women's Wear",
    subcategory: 'Cover-Ups',
    price: 3.00,
    msrp: 8.99,
    stock: 250,
    weight: 0.05,
    image: '/images/products/scarf-rasta-bobmarley.webp',
    description: 'Rasta Bob Marley print mesh cover-up. Caribbean-inspired beach wear, multi-function design.',
    specs: { material: 'Polyester Mesh', style: 'Cover-Up' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },
  {
    id: 'mesh-beach-tt',
    sku: 'MSH-BT',
    name: 'Trinidad & Tobago Mesh Beach Dress',
    category: "Women's Wear",
    subcategory: 'Beach Dresses',
    price: 3.50,
    msrp: 9.99,
    stock: 200,
    weight: 0.06,
    image: '/images/products/scarf-trinidad-mesh.webp',
    description: 'Trinidad & Tobago flag colors mesh beach dress. Lightweight Caribbean-inspired cover-up.',
    specs: { material: 'Polyester Mesh', size: 'One Size' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },
  {
    id: 'mesh-beach-tt-pair',
    sku: 'MSH-BTP',
    name: 'Trinidad & Tobago Mesh Dress Pair',
    category: "Women's Wear",
    subcategory: 'Beach Dresses',
    price: 4.00,
    msrp: 11.99,
    stock: 150,
    weight: 0.08,
    image: '/images/products/scarf-trinidad-pair.webp',
    description: 'Trinidad & Tobago flag print mesh dress pair. Two complementary Caribbean designs per pack.',
    specs: { material: 'Polyester Mesh', quantity: '2 per pack' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen packs',
  },

  // ── BUCKET HATS (in Caps & Fishing Hats category) ──────────────────
  {
    id: 'bucket-bb',
    sku: 'BKT-BB',
    name: 'Barbados Flag Bucket Hat',
    category: 'Caps & Fishing Hats',
    subcategory: 'Bucket Hats',
    price: 6.00,
    msrp: 16.99,
    stock: 120,
    weight: 0.1,
    image: '/images/products/bucket-hat-barbados.webp',
    description: 'Barbados flag print bucket hat. Full brim sun protection, lightweight.',
    specs: { style: 'Bucket Hat', size: 'One Size', brim: 'Full' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },
  {
    id: 'bucket-cm',
    sku: 'BKT-CM',
    name: 'Cameroon Flag Bucket Hat',
    category: 'Caps & Fishing Hats',
    subcategory: 'Bucket Hats',
    price: 6.00,
    msrp: 16.99,
    stock: 100,
    weight: 0.1,
    image: '/images/products/bucket-hat-cameroon.webp',
    description: 'Cameroon flag print bucket hat. Full brim sun protection, lightweight.',
    specs: { style: 'Bucket Hat', size: 'One Size', brim: 'Full' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },
  {
    id: 'bucket-dm',
    sku: 'BKT-DM',
    name: 'Dominica Flag Bucket Hat',
    category: 'Caps & Fishing Hats',
    subcategory: 'Bucket Hats',
    price: 6.00,
    msrp: 16.99,
    stock: 100,
    weight: 0.1,
    image: '/images/products/bucket-hat-dominica.webp',
    description: 'Dominica flag print bucket hat. Full brim sun protection, lightweight.',
    specs: { style: 'Bucket Hat', size: 'One Size', brim: 'Full' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },
  {
    id: 'bucket-eu',
    sku: 'BKT-EU',
    name: 'European Union Bucket Hat',
    category: 'Caps & Fishing Hats',
    subcategory: 'Bucket Hats',
    price: 6.00,
    msrp: 16.99,
    stock: 80,
    weight: 0.1,
    image: '/images/products/bucket-hat-eu.webp',
    description: 'European Union flag print bucket hat. Full brim sun protection, lightweight.',
    specs: { style: 'Bucket Hat', size: 'One Size', brim: 'Full' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },
  {
    id: 'bucket-ht',
    sku: 'BKT-HT',
    name: 'Haiti Flag Bucket Hat',
    category: 'Caps & Fishing Hats',
    subcategory: 'Bucket Hats',
    price: 6.00,
    msrp: 16.99,
    stock: 120,
    weight: 0.1,
    image: '/images/products/bucket-hat-haiti.webp',
    description: 'Haiti flag print bucket hat. Full brim sun protection, lightweight.',
    specs: { style: 'Bucket Hat', size: 'One Size', brim: 'Full' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },
  {
    id: 'bucket-lc',
    sku: 'BKT-LC',
    name: 'St Lucia Flag Bucket Hat',
    category: 'Caps & Fishing Hats',
    subcategory: 'Bucket Hats',
    price: 6.00,
    msrp: 16.99,
    stock: 100,
    weight: 0.1,
    image: '/images/products/bucket-hat-stlucia.webp',
    description: 'St Lucia flag print bucket hat. Full brim sun protection, lightweight.',
    specs: { style: 'Bucket Hat', size: 'One Size', brim: 'Full' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },
  {
    id: 'bucket-vc',
    sku: 'BKT-VC',
    name: 'St Vincent & the Grenadines Bucket Hat',
    category: 'Caps & Fishing Hats',
    subcategory: 'Bucket Hats',
    price: 6.00,
    msrp: 16.99,
    stock: 80,
    weight: 0.1,
    image: '/images/products/bucket-hat-stvincent.webp',
    description: 'St Vincent & the Grenadines flag print bucket hat. Full brim sun protection.',
    specs: { style: 'Bucket Hat', size: 'One Size', brim: 'Full' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },

  // ── SNAPBACK CAPS (new countries) ──────────────────────────────────
  {
    id: 'cap-bb',
    sku: 'CAP-BB',
    name: 'Barbados Flag Snapback Cap',
    category: 'Caps & Fishing Hats',
    subcategory: 'Snapback Caps',
    price: 5.50,
    msrp: 15.99,
    stock: 150,
    weight: 0.12,
    image: '/images/products/cap-barbados.webp',
    description: 'Barbados flag embroidered snapback cap. Adjustable snap closure, one size fits most.',
    specs: { style: 'Snapback Cap', closure: 'Snap', size: 'One Size' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },
  {
    id: 'cap-bz',
    sku: 'CAP-BZ',
    name: 'Belize Flag Snapback Cap',
    category: 'Caps & Fishing Hats',
    subcategory: 'Snapback Caps',
    price: 5.50,
    msrp: 15.99,
    stock: 100,
    weight: 0.12,
    image: '/images/products/cap-belize.webp',
    description: 'Belize flag embroidered snapback cap. Adjustable snap closure, one size fits most.',
    specs: { style: 'Snapback Cap', closure: 'Snap', size: 'One Size' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },
  {
    id: 'cap-bobmarley',
    sku: 'CAP-BM',
    name: 'Bob Marley One Love Snapback Cap',
    category: 'Caps & Fishing Hats',
    subcategory: 'Snapback Caps',
    price: 5.50,
    msrp: 15.99,
    stock: 200,
    weight: 0.12,
    image: '/images/products/cap-bobmarley.webp',
    description: 'Bob Marley One Love embroidered snapback cap. Rasta colors, adjustable snap closure.',
    specs: { style: 'Snapback Cap', closure: 'Snap', size: 'One Size' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },
  {
    id: 'cap-dm',
    sku: 'CAP-DM',
    name: 'Dominica Flag Snapback Cap',
    category: 'Caps & Fishing Hats',
    subcategory: 'Snapback Caps',
    price: 5.50,
    msrp: 15.99,
    stock: 100,
    weight: 0.12,
    image: '/images/products/cap-dominica.webp',
    description: 'Dominica flag embroidered snapback cap. Adjustable snap closure, one size fits most.',
    specs: { style: 'Snapback Cap', closure: 'Snap', size: 'One Size' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },
  {
    id: 'cap-gd',
    sku: 'CAP-GD',
    name: 'Grenada Flag Snapback Cap',
    category: 'Caps & Fishing Hats',
    subcategory: 'Snapback Caps',
    price: 5.50,
    msrp: 15.99,
    stock: 100,
    weight: 0.12,
    image: '/images/products/cap-grenada.webp',
    description: 'Grenada flag embroidered snapback cap. Adjustable snap closure, one size fits most.',
    specs: { style: 'Snapback Cap', closure: 'Snap', size: 'One Size' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },
  {
    id: 'cap-gy',
    sku: 'CAP-GY',
    name: 'Guyana Flag Snapback Cap',
    category: 'Caps & Fishing Hats',
    subcategory: 'Snapback Caps',
    price: 5.50,
    msrp: 15.99,
    stock: 120,
    weight: 0.12,
    image: '/images/products/cap-guyana.webp',
    description: 'Guyana flag embroidered snapback cap. Adjustable snap closure, one size fits most.',
    specs: { style: 'Snapback Cap', closure: 'Snap', size: 'One Size' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },
  {
    id: 'cap-ht',
    sku: 'CAP-HT',
    name: 'Haiti Flag Snapback Cap',
    category: 'Caps & Fishing Hats',
    subcategory: 'Snapback Caps',
    price: 5.50,
    msrp: 15.99,
    stock: 150,
    weight: 0.12,
    image: '/images/products/cap-haiti.webp',
    description: 'Haiti flag embroidered snapback cap. Adjustable snap closure, one size fits most.',
    specs: { style: 'Snapback Cap', closure: 'Snap', size: 'One Size' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },
  {
    id: 'cap-lc',
    sku: 'CAP-LC',
    name: 'St Lucia Flag Snapback Cap',
    category: 'Caps & Fishing Hats',
    subcategory: 'Snapback Caps',
    price: 5.50,
    msrp: 15.99,
    stock: 120,
    weight: 0.12,
    image: '/images/products/cap-stlucia.webp',
    description: 'St Lucia flag embroidered snapback cap. Adjustable snap closure, one size fits most.',
    specs: { style: 'Snapback Cap', closure: 'Snap', size: 'One Size' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },

  // ── VISORS ─────────────────────────────────────────────────────────
  {
    id: 'visor-gd',
    sku: 'VIS-GD',
    name: 'Grenada Flag Visor',
    category: 'Caps & Fishing Hats',
    subcategory: 'Visors',
    price: 4.50,
    msrp: 12.99,
    stock: 100,
    weight: 0.08,
    image: '/images/products/visor-grenada.webp',
    description: 'Grenada flag print sun visor. Open-top design, adjustable strap.',
    specs: { style: 'Sun Visor', closure: 'Adjustable', size: 'One Size' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },
  {
    id: 'visor-kn',
    sku: 'VIS-KN',
    name: 'St Kitts & Nevis Flag Visor',
    category: 'Caps & Fishing Hats',
    subcategory: 'Visors',
    price: 4.50,
    msrp: 12.99,
    stock: 80,
    weight: 0.08,
    image: '/images/products/visor-stkitts.webp',
    description: 'St Kitts & Nevis flag print sun visor. Open-top design, adjustable strap.',
    specs: { style: 'Sun Visor', closure: 'Adjustable', size: 'One Size' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },
  {
    id: 'visor-vc',
    sku: 'VIS-VC',
    name: 'St Vincent & the Grenadines Visor',
    category: 'Caps & Fishing Hats',
    subcategory: 'Visors',
    price: 4.50,
    msrp: 12.99,
    stock: 80,
    weight: 0.08,
    image: '/images/products/visor-stvincent.webp',
    description: 'St Vincent & the Grenadines flag print sun visor. Open-top design, adjustable strap.',
    specs: { style: 'Sun Visor', closure: 'Adjustable', size: 'One Size' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },
  {
    id: 'visor-tt',
    sku: 'VIS-TT',
    name: 'Trinidad & Tobago Flag Visor',
    category: 'Caps & Fishing Hats',
    subcategory: 'Visors',
    price: 4.50,
    msrp: 12.99,
    stock: 120,
    weight: 0.08,
    image: '/images/products/visor-trinidad.webp',
    description: 'Trinidad & Tobago flag print sun visor. Open-top design, adjustable strap.',
    specs: { style: 'Sun Visor', closure: 'Adjustable', size: 'One Size' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },

  // ── NEW KEYCHAINS ──────────────────────────────────────────────────
  {
    id: 'keychain-dm',
    sku: 'KEY-DM',
    name: 'Dominica Flag Bottle Opener Keychain',
    category: 'Keychains & Collectables',
    subcategory: 'Bottle Openers',
    price: 1.75,
    msrp: 5.99,
    stock: 300,
    weight: 0.04,
    image: '/images/products/keychain-dominica.webp',
    description: 'Dominica flag bottle opener keychain. Dual-function with ring clip.',
    specs: { material: 'Metal / Enamel', type: 'Bottle Opener', size: '3"' },
    boxQty: 24,
    moq: 24,
    unit: 'per box of 24',
  },
  {
    id: 'keychain-gy',
    sku: 'KEY-GY',
    name: 'Guyana Flag Bottle Opener Keychain',
    category: 'Keychains & Collectables',
    subcategory: 'Bottle Openers',
    price: 1.75,
    msrp: 5.99,
    stock: 300,
    weight: 0.04,
    image: '/images/products/keychain-guyana.webp',
    description: 'Guyana flag bottle opener keychain. Dual-function with ring clip.',
    specs: { material: 'Metal / Enamel', type: 'Bottle Opener', size: '3"' },
    boxQty: 24,
    moq: 24,
    unit: 'per box of 24',
  },
  {
    id: 'keychain-ht',
    sku: 'KEY-HT',
    name: 'Haiti Flag Bottle Opener Keychain',
    category: 'Keychains & Collectables',
    subcategory: 'Bottle Openers',
    price: 1.75,
    msrp: 5.99,
    stock: 250,
    weight: 0.04,
    image: '/images/products/keychain-haiti.webp',
    description: 'Haiti flag bottle opener keychain. Dual-function with ring clip.',
    specs: { material: 'Metal / Enamel', type: 'Bottle Opener', size: '3"' },
    boxQty: 24,
    moq: 24,
    unit: 'per box of 24',
  },
  {
    id: 'keychain-lc',
    sku: 'KEY-LC',
    name: 'St Lucia Flag Bottle Opener Keychain',
    category: 'Keychains & Collectables',
    subcategory: 'Bottle Openers',
    price: 1.75,
    msrp: 5.99,
    stock: 200,
    weight: 0.04,
    image: '/images/products/keychain-stlucia.webp',
    description: 'St Lucia flag bottle opener keychain. Dual-function with ring clip.',
    specs: { material: 'Metal / Enamel', type: 'Bottle Opener', size: '3"' },
    boxQty: 24,
    moq: 24,
    unit: 'per box of 24',
  },
  {
    id: 'mirror-tt-gloves',
    sku: 'MIR-TT-G',
    name: 'Trinidad & Tobago Mini Boxing Gloves',
    category: 'Car Mirror Trinkets',
    subcategory: 'Boxing Gloves',
    price: 2.00,
    msrp: 6.99,
    stock: 250,
    weight: 0.05,
    image: '/images/products/keychain-trinidad-gloves.webp',
    description: 'Trinidad & Tobago flag mini boxing gloves. Rearview mirror hanging style with string.',
    specs: { material: 'Vinyl / Fabric', style: 'Mini Boxing Gloves', attachment: 'Hanging String' },
    boxQty: 24,
    moq: 24,
    unit: 'per box of 24',
  },
  {
    id: 'mirror-jm-gloves',
    sku: 'MIR-JM-G',
    name: 'Jamaica Mini Boxing Gloves',
    category: 'Car Mirror Trinkets',
    subcategory: 'Boxing Gloves',
    price: 2.00,
    msrp: 6.99,
    stock: 250,
    weight: 0.05,
    image: '/images/products/gloves-jamaica.webp',
    description: 'Jamaica flag mini boxing gloves. Rearview mirror hanging style with string.',
    specs: { material: 'Vinyl / Fabric', style: 'Mini Boxing Gloves', attachment: 'Hanging String' },
    boxQty: 24,
    moq: 24,
    unit: 'per box of 24',
  },
  {
    id: 'mirror-gy-gloves',
    sku: 'MIR-GY-G',
    name: 'Guyana Mini Boxing Gloves',
    category: 'Car Mirror Trinkets',
    subcategory: 'Boxing Gloves',
    price: 2.00,
    msrp: 6.99,
    stock: 250,
    weight: 0.05,
    image: '/images/products/gloves-guyana.webp',
    description: 'Guyana flag mini boxing gloves. Rearview mirror hanging style with string.',
    specs: { material: 'Vinyl / Fabric', style: 'Mini Boxing Gloves', attachment: 'Hanging String' },
    boxQty: 24,
    moq: 24,
    unit: 'per box of 24',
  },
  {
    id: 'mirror-bb-gloves',
    sku: 'MIR-BB-G',
    name: 'Barbados Mini Boxing Gloves',
    category: 'Car Mirror Trinkets',
    subcategory: 'Boxing Gloves',
    price: 2.00,
    msrp: 6.99,
    stock: 250,
    weight: 0.05,
    image: '/images/products/gloves-barbados.webp',
    description: 'Barbados flag mini boxing gloves. Rearview mirror hanging style with string.',
    specs: { material: 'Vinyl / Fabric', style: 'Mini Boxing Gloves', attachment: 'Hanging String' },
    boxQty: 24,
    moq: 24,
    unit: 'per box of 24',
  },
  {
    id: 'mirror-ht-gloves',
    sku: 'MIR-HT-G',
    name: 'Haiti Mini Boxing Gloves',
    category: 'Car Mirror Trinkets',
    subcategory: 'Boxing Gloves',
    price: 2.00,
    msrp: 6.99,
    stock: 250,
    weight: 0.05,
    image: '/images/products/gloves-haiti.webp',
    description: 'Haiti flag mini boxing gloves. Rearview mirror hanging style with string.',
    specs: { material: 'Vinyl / Fabric', style: 'Mini Boxing Gloves', attachment: 'Hanging String' },
    boxQty: 24,
    moq: 24,
    unit: 'per box of 24',
  },
  {
    id: 'mirror-ag-gloves',
    sku: 'MIR-AG-G',
    name: 'Antigua & Barbuda Mini Boxing Gloves',
    category: 'Car Mirror Trinkets',
    subcategory: 'Boxing Gloves',
    price: 2.00,
    msrp: 6.99,
    stock: 250,
    weight: 0.05,
    image: '/images/products/gloves-antigua.webp',
    description: 'Antigua & Barbuda flag mini boxing gloves. Rearview mirror hanging style with string.',
    specs: { material: 'Vinyl / Fabric', style: 'Mini Boxing Gloves', attachment: 'Hanging String' },
    boxQty: 24,
    moq: 24,
    unit: 'per box of 24',
  },
  {
    id: 'keychain-tt-shoe',
    sku: 'KEY-TT-S',
    name: 'Trinidad & Tobago Shoe Keychain',
    category: 'Keychains & Collectables',
    subcategory: 'Novelty',
    price: 2.00,
    msrp: 6.99,
    stock: 200,
    weight: 0.04,
    image: '/images/products/keychain-trinidad-shoe.webp',
    description: 'Trinidad & Tobago flag print miniature shoe keychain. Collectible novelty design.',
    specs: { material: 'Resin / Enamel', style: 'Mini Shoe' },
    boxQty: 24,
    moq: 24,
    unit: 'per box of 24',
  },

  // ── NEW SUNGLASSES ─────────────────────────────────────────────────
  {
    id: 'sunglass-dm',
    sku: 'SUN-DM',
    name: 'Dominica Flag Sunglasses',
    category: 'Flag Sunglasses',
    price: 3.00,
    msrp: 9.99,
    stock: 200,
    weight: 0.04,
    image: '/images/products/sunglass-dominica.webp',
    description: 'Dominica flag print sunglasses. UV400 protection, lightweight plastic frame.',
    specs: { protection: 'UV400', frame: 'Plastic', style: 'Wayfarer' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },
  {
    id: 'sunglass-kn',
    sku: 'SUN-KN',
    name: 'St Kitts & Nevis Flag Sunglasses',
    category: 'Flag Sunglasses',
    price: 3.00,
    msrp: 9.99,
    stock: 150,
    weight: 0.04,
    image: '/images/products/sunglass-stkitts.webp',
    description: 'St Kitts & Nevis flag print sunglasses. UV400 protection, lightweight plastic frame.',
    specs: { protection: 'UV400', frame: 'Plastic', style: 'Wayfarer' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },

  // ── NEW EARRINGS ───────────────────────────────────────────────────
  {
    id: 'earring-ag-nested',
    sku: 'EAR-AG-N',
    name: 'Antigua & Barbuda Nested Ring Earrings',
    category: "Women's Wear",
    subcategory: 'Nested Ring',
    price: 2.50,
    msrp: 8.99,
    stock: 200,
    weight: 0.02,
    image: '/images/products/earring-antigua-nested.webp',
    description: 'Antigua & Barbuda flag nested ring earrings. Layered hoop design, hypoallergenic.',
    specs: { material: 'Alloy / Enamel', size: '50mm', type: 'Nested Ring' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen pairs',
  },
  {
    id: 'earring-ag-round',
    sku: 'EAR-AG-R',
    name: 'Antigua & Barbuda Round Earrings',
    category: "Women's Wear",
    subcategory: 'Round',
    price: 2.00,
    msrp: 7.99,
    stock: 200,
    weight: 0.02,
    image: '/images/products/earring-antigua-round.webp',
    description: 'Antigua & Barbuda flag round disc earrings. Flat disc style, hypoallergenic.',
    specs: { material: 'Alloy / Enamel', size: '40mm', type: 'Round Disc' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen pairs',
  },
  {
    id: 'earring-bb-round',
    sku: 'EAR-BB-R',
    name: 'Barbados Flag Round Earrings',
    category: "Women's Wear",
    subcategory: 'Round',
    price: 2.00,
    msrp: 7.99,
    stock: 200,
    weight: 0.02,
    image: '/images/products/earring-barbados-round.webp',
    description: 'Barbados flag round disc earrings. Flat disc style, hypoallergenic.',
    specs: { material: 'Alloy / Enamel', size: '40mm', type: 'Round Disc' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen pairs',
  },
  {
    id: 'earring-black-hoop',
    sku: 'EAR-BLK',
    name: 'Black Round Hoop Earrings',
    category: "Women's Wear",
    subcategory: 'Solid Color',
    price: 1.75,
    msrp: 6.99,
    stock: 300,
    weight: 0.02,
    image: '/images/products/earring-black-hoop.webp',
    description: 'Classic black round hoop earrings. Lightweight, hypoallergenic metal.',
    specs: { material: 'Alloy', size: '50mm', type: 'Hoop', color: 'Black' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen pairs',
  },
  {
    id: 'earring-dm-nested',
    sku: 'EAR-DM-N',
    name: 'Dominica Flag Nested Ring Earrings',
    category: "Women's Wear",
    subcategory: 'Nested Ring',
    price: 2.50,
    msrp: 8.99,
    stock: 150,
    weight: 0.02,
    image: '/images/products/earring-dominica-nested.webp',
    description: 'Dominica flag nested ring earrings. Layered hoop design, hypoallergenic.',
    specs: { material: 'Alloy / Enamel', size: '50mm', type: 'Nested Ring' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen pairs',
  },
  {
    id: 'earring-dm-round',
    sku: 'EAR-DM-R',
    name: 'Dominica Flag Round Earrings',
    category: "Women's Wear",
    subcategory: 'Round',
    price: 2.00,
    msrp: 7.99,
    stock: 150,
    weight: 0.02,
    image: '/images/products/earring-dominica-round.webp',
    description: 'Dominica flag round disc earrings. Flat disc style, hypoallergenic.',
    specs: { material: 'Alloy / Enamel', size: '40mm', type: 'Round Disc' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen pairs',
  },
  {
    id: 'earring-gy-nested',
    sku: 'EAR-GY-N',
    name: 'Guyana Flag Nested Ring Earrings',
    category: "Women's Wear",
    subcategory: 'Nested Ring',
    price: 2.50,
    msrp: 8.99,
    stock: 150,
    weight: 0.02,
    image: '/images/products/earring-guyana-nested.webp',
    description: 'Guyana flag nested ring earrings. Layered hoop design, hypoallergenic.',
    specs: { material: 'Alloy / Enamel', size: '50mm', type: 'Nested Ring' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen pairs',
  },

  // ── WOMEN'S WEAR: ANKH JEWELRY ─────────────────────────────────────
  {
    id: 'ankh-dangle-gold',
    sku: 'ANK-DG',
    name: 'Ankh Dangle Earrings (Gold)',
    category: "Women's Wear",
    subcategory: 'Ankh Jewelry',
    price: 3.00,
    msrp: 9.99,
    stock: 200,
    weight: 0.02,
    image: '/images/products/ankh-dangle-earrings-gold.webp',
    description: 'Gold-tone ankh dangle earrings. Egyptian cross symbol, lightweight drop style.',
    specs: { material: 'Alloy / Gold Plated', size: '60mm drop', type: 'Dangle' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen pairs',
  },
  {
    id: 'ankh-dangle-silver',
    sku: 'ANK-DS',
    name: 'Ankh Dangle Earrings (Silver)',
    category: "Women's Wear",
    subcategory: 'Ankh Jewelry',
    price: 3.00,
    msrp: 9.99,
    stock: 200,
    weight: 0.02,
    image: '/images/products/ankh-dangle-earrings-silver.webp',
    description: 'Silver-tone ankh dangle earrings. Egyptian cross symbol, lightweight drop style.',
    specs: { material: 'Alloy / Silver Plated', size: '60mm drop', type: 'Dangle' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen pairs',
  },
  {
    id: 'ankh-pendant-collection',
    sku: 'ANK-PC',
    name: 'Ankh Pendant Collection (Silver)',
    category: "Women's Wear",
    subcategory: 'Ankh Jewelry',
    price: 5.00,
    msrp: 14.99,
    stock: 100,
    weight: 0.05,
    image: '/images/products/ankh-pendant-collection-silver.webp',
    description: 'Silver-tone ankh pendant collection. Multiple ankh cross designs in one set.',
    specs: { material: 'Alloy / Silver Plated', type: 'Pendant Set' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen sets',
  },
  {
    id: 'ankh-pendant-necklace',
    sku: 'ANK-PN',
    name: 'Ankh Pendant Necklace (Silver)',
    category: "Women's Wear",
    subcategory: 'Ankh Jewelry',
    price: 4.00,
    msrp: 12.99,
    stock: 150,
    weight: 0.04,
    image: '/images/products/ankh-pendant-necklace-silver.webp',
    description: 'Silver-tone ankh pendant necklace with chain. Classic Egyptian cross design.',
    specs: { material: 'Alloy / Silver Plated', chain: '24"', type: 'Pendant Necklace' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },

  // ── NEW SMUDGE STICKS ─────────────────────────────────────────────
  {
    id: 'smudge-cedar',
    sku: 'SMG-CD',
    name: 'Cedar Smudge Stick',
    category: 'Smudge Sticks',
    subcategory: 'Cedar',
    price: 3.50,
    msrp: 9.99,
    stock: 200,
    weight: 0.07,
    image: '/images/products/smudge-cedar.webp',
    description: 'Cedar smudge stick for cleansing and purification. Warm, woodsy aroma.',
    specs: { type: 'Cedar', size: '4-5"', origin: 'North America' },
    boxQty: 24,
    moq: 24,
    unit: 'per box of 24',
  },
  {
    id: 'smudge-hierba-santa',
    sku: 'SMG-HS',
    name: 'Hierba Santa Smudge Stick',
    category: 'Smudge Sticks',
    subcategory: 'Hierba Santa',
    price: 4.00,
    msrp: 10.99,
    stock: 150,
    weight: 0.08,
    image: '/images/products/smudge-hierba-santa.webp',
    description: 'Hierba Santa (Holy Herb) smudge stick. Used for spiritual healing and protection rituals.',
    specs: { type: 'Hierba Santa', size: '4-5"', origin: 'California' },
    boxQty: 24,
    moq: 24,
    unit: 'per box of 24',
  },
  {
    id: 'smudge-sage-rose',
    sku: 'SMG-SR',
    name: 'White Sage & Rose Petals Smudge Stick',
    category: 'Smudge Sticks',
    subcategory: 'White Sage Blends',
    price: 4.50,
    msrp: 12.99,
    stock: 150,
    weight: 0.08,
    image: '/images/products/smudge-sage-rose.webp',
    description: 'White sage smudge stick wrapped with dried rose petals. For love, healing, and purification.',
    specs: { type: 'White Sage + Rose', size: '4-5"', origin: 'California' },
    boxQty: 24,
    moq: 24,
    unit: 'per box of 24',
  },
  {
    id: 'smudge-sage-blue-flowers',
    sku: 'SMG-SBF',
    name: 'White Sage & Blue Flowers Smudge Stick',
    category: 'Smudge Sticks',
    subcategory: 'White Sage Blends',
    price: 4.50,
    msrp: 12.99,
    stock: 120,
    weight: 0.08,
    image: '/images/products/smudge-sage-blue-flowers.webp',
    description: 'White sage smudge stick with blue sinuata flowers. For tranquility and spiritual peace.',
    specs: { type: 'White Sage + Blue Flowers', size: '4-5"', origin: 'California' },
    boxQty: 24,
    moq: 24,
    unit: 'per box of 24',
  },
  {
    id: 'smudge-sage-eucalyptus',
    sku: 'SMG-SE',
    name: 'White Sage & Eucalyptus Smudge Stick',
    category: 'Smudge Sticks',
    subcategory: 'White Sage Blends',
    price: 4.50,
    msrp: 12.99,
    stock: 150,
    weight: 0.08,
    image: '/images/products/smudge-sage-eucalyptus.webp',
    description: 'White sage smudge stick with eucalyptus leaves. For clarity, healing, and respiratory wellness.',
    specs: { type: 'White Sage + Eucalyptus', size: '4-5"', origin: 'California' },
    boxQty: 24,
    moq: 24,
    unit: 'per box of 24',
  },
  {
    id: 'smudge-sage-lavender',
    sku: 'SMG-SL',
    name: 'White Sage & Lavender Smudge Stick',
    category: 'Smudge Sticks',
    subcategory: 'White Sage Blends',
    price: 4.50,
    msrp: 12.99,
    stock: 180,
    weight: 0.08,
    image: '/images/products/smudge-sage-lavender.webp',
    description: 'White sage smudge stick with dried lavender. For relaxation, calm, and peaceful sleep.',
    specs: { type: 'White Sage + Lavender', size: '4-5"', origin: 'California' },
    boxQty: 24,
    moq: 24,
    unit: 'per box of 24',
  },
  {
    id: 'smudge-sage-red-lavender',
    sku: 'SMG-SRL',
    name: 'White Sage & Red Lavender Smudge Stick',
    category: 'Smudge Sticks',
    subcategory: 'White Sage Blends',
    price: 4.50,
    msrp: 12.99,
    stock: 120,
    weight: 0.08,
    image: '/images/products/smudge-sage-red-lavender.webp',
    description: 'White sage smudge stick with red lavender flowers. For passion, protection, and energy.',
    specs: { type: 'White Sage + Red Lavender', size: '4-5"', origin: 'California' },
    boxQty: 24,
    moq: 24,
    unit: 'per box of 24',
  },
  {
    id: 'smudge-sage-multisize',
    sku: 'SMG-MS',
    name: 'White Sage Smudge Sticks (Multi-Size Pack)',
    category: 'Smudge Sticks',
    subcategory: 'White Sage',
    price: 8.00,
    msrp: 19.99,
    stock: 100,
    weight: 0.2,
    image: '/images/products/smudge-sage-multisize.webp',
    description: 'White sage smudge stick assortment with multiple sizes. Great for sampling or variety display.',
    specs: { type: 'White Sage', sizes: 'Assorted (3"-9")', origin: 'California' },
    boxQty: 12,
    moq: 12,
    unit: 'per box of 12 packs',
  },
  {
    id: 'smudge-sage-three-roses',
    sku: 'SMG-STR',
    name: 'White Sage with Three Roses Smudge Stick',
    category: 'Smudge Sticks',
    subcategory: 'White Sage Blends',
    price: 5.00,
    msrp: 13.99,
    stock: 100,
    weight: 0.09,
    image: '/images/products/smudge-sage-three-roses.webp',
    description: 'White sage smudge stick adorned with three dried rosebuds. For love, beauty, and spiritual cleansing.',
    specs: { type: 'White Sage + Three Roses', size: '4-5"', origin: 'California' },
    boxQty: 24,
    moq: 24,
    unit: 'per box of 24',
  },

  // ── FLAG ON STICK ──────────────────────────────────────────────────
  {
    id: 'flag-tt-stick',
    sku: 'FLG-TT-S',
    name: 'Trinidad & Tobago Flag on Stick',
    category: 'Full Size Flags',
    subcategory: 'Flags on Stick',
    price: 2.00,
    msrp: 5.99,
    stock: 300,
    weight: 0.08,
    image: '/images/products/flag-trinidad-stick.webp',
    description: 'Trinidad & Tobago small flag on wooden stick. Great for parades, events, and car decoration.',
    specs: { size: '12x18"', material: 'Polyester', stick: 'Wood' },
    boxQty: 24,
    moq: 24,
    unit: 'per box of 24',
  },

  // ── NEW CAR MIRROR TRINKETS ────────────────────────────────────────
  {
    id: 'mirror-ca',
    sku: 'MIR-CA',
    name: 'Canada Car Side Mirror Flags',
    category: 'Car Mirror Trinkets',
    subcategory: 'Side Mirror Flags',
    price: 3.00,
    msrp: 8.99,
    stock: 200,
    weight: 0.06,
    image: '/images/products/mirror-canada.webp',
    description: 'Canada flag car side mirror covers. Pair of stretchy flag covers for side mirrors.',
    specs: { style: 'Side Mirror Covers', quantity: 'Pair', attachment: 'Elastic' },
    boxQty: 24,
    moq: 24,
    unit: 'per box of 24 pairs',
  },

  // ── MESH CLOTHING ──────────────────────────────────────────────────
  {
    id: 'mesh-caribbean-dress',
    sku: 'MSH-CD',
    name: 'Caribbean Mesh Dress',
    category: "Women's Wear",
    subcategory: 'Dresses',
    price: 8.00,
    msrp: 22.99,
    stock: 80,
    weight: 0.15,
    image: '/images/products/mesh-caribbean-dress.webp',
    description: 'Caribbean-themed mesh dress. Colorful tropical design, lightweight see-through fabric.',
    specs: { material: 'Polyester Mesh', size: 'One Size', style: 'Dress' },
    boxQty: 6,
    moq: 6,
    unit: 'per half dozen',
  },
  {
    id: 'mesh-jamaica-tank',
    sku: 'MSH-JT',
    name: 'Jamaica Mesh Tank Top',
    category: "Women's Wear",
    subcategory: 'Tops',
    price: 5.00,
    msrp: 14.99,
    stock: 120,
    weight: 0.1,
    image: '/images/products/mesh-jamaica-tank.webp',
    description: 'Jamaica flag colors mesh tank top. Green, gold, and black string vest style.',
    specs: { material: 'Polyester Mesh', size: 'One Size', style: 'Tank Top' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },
  {
    id: 'mesh-orange-dress',
    sku: 'MSH-OD',
    name: 'Orange Mesh Dress',
    category: "Women's Wear",
    subcategory: 'Dresses',
    price: 8.00,
    msrp: 22.99,
    stock: 100,
    weight: 0.15,
    image: '/images/products/mesh-orange-dress.webp',
    description: 'Vibrant orange mesh dress. Lightweight see-through fabric, festival and beach style.',
    specs: { material: 'Polyester Mesh', sizes: 'S-M / L-XL', style: 'Dress' },
    boxQty: 6,
    moq: 6,
    unit: 'per half dozen',
  },
  {
    id: 'mesh-rasta-shorts-set',
    sku: 'MSH-RS',
    name: 'Rasta Mesh Shorts & Crop Top Set',
    category: "Women's Wear",
    subcategory: 'Sets',
    price: 10.00,
    msrp: 27.99,
    stock: 60,
    weight: 0.18,
    image: '/images/products/mesh-rasta-shorts-set.webp',
    description: 'Rasta colors mesh shorts and crop top matching set. Red, gold, green design.',
    specs: { material: 'Polyester Mesh', size: 'One Size', pieces: '2 (Top + Shorts)' },
    boxQty: 6,
    moq: 6,
    unit: 'per half dozen sets',
  },
  {
    id: 'mesh-rasta-dress',
    sku: 'MSH-RD',
    name: 'Rasta Mesh Sleeveless Dress',
    category: "Women's Wear",
    subcategory: 'Dresses',
    price: 8.00,
    msrp: 22.99,
    stock: 80,
    weight: 0.15,
    image: '/images/products/mesh-rasta-dress.webp',
    description: 'Rasta colors mesh sleeveless dress. Red, gold, and green stripes, lightweight fabric.',
    specs: { material: 'Polyester Mesh', size: 'One Size', style: 'Sleeveless Dress' },
    boxQty: 6,
    moq: 6,
    unit: 'per half dozen',
  },
  {
    id: 'mesh-rasta-tank',
    sku: 'MSH-RT',
    name: 'Rasta Mesh Tank Top',
    category: "Women's Wear",
    subcategory: 'Tops',
    price: 5.00,
    msrp: 14.99,
    stock: 120,
    weight: 0.1,
    image: '/images/products/mesh-rasta-tank.webp',
    description: 'Rasta colors mesh tank top. Red, gold, and green string vest style.',
    specs: { material: 'Polyester Mesh', size: 'One Size', style: 'Tank Top' },
    boxQty: 12,
    moq: 12,
    unit: 'per dozen',
  },

  // ── WINDBREAKERS ───────────────────────────────────────────────────
  {
    id: 'windbreaker-tt',
    sku: 'WND-TT',
    name: 'Trinidad & Tobago Windbreaker Jacket',
    category: 'Windbreakers',
    price: 15.00,
    msrp: 39.99,
    stock: 60,
    weight: 0.35,
    image: '/images/products/windbreaker-trinidad.webp',
    description: 'Trinidad & Tobago flag windbreaker jacket. Lightweight, water-resistant, full zip.',
    specs: { material: 'Polyester', sizes: 'S-XXL', style: 'Full Zip', waterResistant: 'Yes' },
    boxQty: 6,
    moq: 6,
    unit: 'per half dozen',
  },
  {
    id: 'windbreaker-tt-cartoon',
    sku: 'WND-TT-C',
    name: 'Trinidad & Tobago Cartoon Windbreaker',
    category: 'Windbreakers',
    price: 15.00,
    msrp: 39.99,
    stock: 50,
    weight: 0.35,
    image: '/images/products/windbreaker-trinidad-cartoon.webp',
    description: 'Trinidad & Tobago windbreaker jacket with cartoon flag design. Fun, colorful graphic print.',
    specs: { material: 'Polyester', sizes: 'S-XXL', style: 'Full Zip', design: 'Cartoon' },
    boxQty: 6,
    moq: 6,
    unit: 'per half dozen',
  },
];
